import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '../lib/db'
import { useAuth } from '../contexts/AuthContext'
import { deriveEventStatus, EVENT_STATUS_LABELS } from '../lib/golf'

export default function EventsPage() {
  const { isCommissioner, player } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [eventsRes, draftsRes] = await Promise.all([
          player?.id
            ? db('list_my_events', { player_id: player.id }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          player?.id
            ? db('list_my_drafts', { owner_id: player.id }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ])
        setEvents(eventsRes?.data || [])
        setDrafts(draftsRes?.data || [])
      } catch {}
      setLoading(false)
    }
    load()
  }, [player?.id])

  return (
    <div className="page">
      <div className="container">
        <div style={{ paddingTop: 12, paddingBottom: 10, marginBottom: 12, borderBottom: '1px solid var(--green-mid)' }}>
          <p className="eyebrow" style={{ marginBottom: 2 }}>Events</p>
          <h1 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.6rem)', margin: 0 }}>Outings & Tournaments</h1>
        </div>

        {isCommissioner && (
          <button
            className="btn btn-primary btn-full"
            style={{ marginBottom: 12, padding: '10px 16px', fontSize: '0.9rem' }}
            onClick={() => navigate('/events/new')}
          >
            Create Event
          </button>
        )}

        {drafts.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p className="text-xs text-muted" style={{ marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Your drafts
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {drafts.map(d => (
                <div
                  key={d.id}
                  className="card"
                  style={{ cursor: 'pointer', padding: '10px 14px', borderColor: 'var(--gold)' }}
                  onClick={() => navigate(`/event/${d.id}`)}
                >
                  <div className="flex justify-between items-center" style={{ marginBottom: 2 }}>
                    <span className="text-mono text-xs text-muted">{d.year} · {d.type || 'outing'}</span>
                    <span className="badge badge-gold">Draft</span>
                  </div>
                  <h3 style={{ fontSize: '1rem', margin: 0, lineHeight: 1.25 }}>{d.name || 'Untitled event'}</h3>
                  <p className="text-xs text-muted" style={{ marginTop: 2 }}>
                    {d.players_scheduled ? `${d.players_scheduled} players × ${d.rounds_scheduled} rounds` : 'Not sized yet'}
                    {d.players_scheduled && d.rounds_scheduled
                      ? (d.players_scheduled <= 8 && d.rounds_scheduled <= 1
                          ? ' · free tier'
                          : ` · $${d.players_scheduled * d.rounds_scheduled} at publish`)
                      : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <p className="text-muted text-sm">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ fontSize: '2rem', marginBottom: 8 }}>📋</p>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>No events yet</p>
            <p className="text-muted text-sm">
              {isCommissioner
                ? 'Create your first event to get started.'
                : 'Events you\'re part of will appear here.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {events.map(evt => {
              // WS-B events are created through the sizing wizard, so they
              // always carry players_scheduled (> 0, required at draft
              // creation). Legacy pre-WS-B events use the status flow and have
              // players_scheduled NULL. Discriminate on players_scheduled, NOT
              // slug/owner_id — the WS-B backfill populated slug + owner_id on
              // legacy rows too, so those would misclassify legacy events as
              // WS-B. Route each to its own landing:
              //   WS-B → /event/:id (EventLandingPage: owner-draft admin OR
              //         participant view)
              //   Legacy → /leaderboard (the pre-WS-B single-event
              //         leaderboard state=event_id shape).
              const isWsB = evt.players_scheduled != null
              // Legacy outings are historical (completed_at backfilled in
              // Phase 2); the dropped status column is no longer read. A legacy
              // row without completed_at is treated as not-yet-final (its
              // leaderboard isn't meaningful, so the card stays non-clickable).
              const isLegacyUpcoming = !isWsB && !evt.completed_at
              // Badge: WS-B events use the DERIVED lifecycle status (D-341).
              // This list doesn't load per-event scores, so a live WS-B event
              // reads 'Upcoming' here; 'In Progress' surfaces on the leaderboard
              // where scores are loaded. completed_at/status='complete' still
              // resolve to 'Complete' without them (see deriveEventStatus).
              // Legacy (pre-WS-B) events have published_at NULL by nature, which
              // deriveEventStatus reads as 'draft' — wrong for them — so they
              // keep the old status-based badge (Complete/Upcoming/Active).
              let badgeText, badgeClass
              if (isWsB) {
                const derivedStatus = deriveEventStatus(evt, evt.event_rounds || [], [])
                badgeText = EVENT_STATUS_LABELS[derivedStatus] || 'Upcoming'
                badgeClass = (derivedStatus === 'in_progress' || derivedStatus === 'registration')
                  ? 'badge-gold' : 'badge-gray'
              } else {
                // Legacy (pre-WS-B) outings: the dropped `status` column is no
                // longer read — completed_at (Phase-2 backfill) is the done
                // marker. Every historical outing is complete.
                badgeText = evt.completed_at ? 'Complete' : 'Upcoming'
                badgeClass = 'badge-gray'
              }
              return (
                <div
                  key={evt.id}
                  className="card"
                  style={{
                    cursor: isLegacyUpcoming ? 'default' : 'pointer',
                    opacity: isLegacyUpcoming ? 0.7 : 1,
                    padding: '10px 14px',
                  }}
                  onClick={() => {
                    if (isLegacyUpcoming) return
                    if (isWsB) navigate(`/event/${evt.id}`)
                    else navigate('/leaderboard', { state: { event_id: evt.id } })
                  }}
                >
                  <div className="flex justify-between items-center" style={{ marginBottom: 2 }}>
                    <span className="text-mono text-xs text-muted">
                      {evt.year}{isWsB && evt.type ? ` · ${evt.type}` : ''}
                    </span>
                    <span className={`badge ${badgeClass}`}>{badgeText}</span>
                  </div>
                  <h3 style={{ fontSize: '1rem', margin: 0, lineHeight: 1.25 }}>{evt.name || 'Golf Outing'}</h3>
                  {(evt.event_rounds || []).find(r => r.course_name)?.course_name && (
                    <p className="text-sm text-muted" style={{ marginTop: 2, fontSize: '0.8rem' }}>{(evt.event_rounds || []).find(r => r.course_name).course_name}</p>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="card" style={{ textAlign: 'center', padding: 16, marginTop: 14 }}>
          <p style={{ fontSize: '1.1rem', marginBottom: 4 }}>🏆</p>
          <h3 style={{ marginBottom: 2, fontSize: '0.95rem' }}>Leagues</h3>
          <p className="text-muted text-sm" style={{ fontSize: '0.78rem' }}>
            Season-long leagues with standings and matchups. Coming soon.
          </p>
        </div>
      </div>
    </div>
  )
}
